import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pencil, Plus, StickyNote } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import type {
  HotboxEpisodeNotePreview,
  HotboxTaskResponse,
} from '../../services/api/types.gen';
import { formatEpisodeNoteTimestamp } from '../../utils/episodeNotes';

export type HotboxNotesCellProps = {
  task: HotboxTaskResponse;
  onOpen: (
    task: HotboxTaskResponse,
    event: React.MouseEvent<HTMLButtonElement>
  ) => void;
};

function sortEpisodeNotesNewestFirst(
  notes: HotboxEpisodeNotePreview[]
): HotboxEpisodeNotePreview[] {
  return [...notes].sort(
    (left, right) =>
      new Date(right.created_at).getTime() - new Date(left.created_at).getTime()
  );
}

const EpisodeNotesPill: React.FC<{
  notes: HotboxEpisodeNotePreview[];
  patientName: string;
}> = ({ notes, patientName }) => {
  const newest = notes[0];
  if (!newest) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type='button'
          className='inline-flex max-w-[9rem] shrink-0 cursor-pointer items-center gap-1 rounded border border-purple-200 bg-purple-50 px-1.5 py-0.5 text-xs font-medium text-purple-700 hover:bg-purple-100'
          data-testid='episode-notes-preview'
          aria-label={`View episode notes for ${patientName}`}
          onClick={event => event.stopPropagation()}
        >
          <StickyNote className='h-3 w-3 shrink-0' aria-hidden='true' />
          <span className='truncate'>{newest.body}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side='top'
        align='end'
        collisionPadding={12}
        className='max-h-80 w-auto max-w-sm overflow-y-auto p-0'
      >
        <div className='px-3 py-2' data-testid='episode-notes-tooltip'>
          <p className='text-sm font-semibold'>Episode notes</p>
          <div className='mt-1 divide-y divide-current/10'>
            {notes.map((note, index) => {
              const timestamp = formatEpisodeNoteTimestamp(note.created_at);
              return (
                <div
                  key={`${note.created_at}-${index}`}
                  className='space-y-1 py-2 first:pt-1 last:pb-0'
                >
                  <p className='text-xs font-semibold'>
                    {note.author_name}
                    {timestamp ? ` · ${timestamp}` : ''}
                  </p>
                  <p className='whitespace-pre-line text-sm leading-snug'>
                    {note.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Hotbox Notes column cell: yellow task sticky (editable) and one purple
 * episode sticky (read-only). Purple opens a popover listing every episode note.
 */
export const HotboxNotesCell: React.FC<HotboxNotesCellProps> = ({
  task,
  onOpen,
}) => {
  const noteRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const episodeNotes = sortEpisodeNotesNewestFirst(
    task.episode_notes_previews ?? []
  );

  const updateTruncation = useCallback(() => {
    const note = noteRef.current;
    setIsTruncated(Boolean(note && note.scrollWidth > note.clientWidth));
  }, []);

  useEffect(() => {
    updateTruncation();
  }, [task.notes, updateTruncation]);

  let taskTrigger: React.ReactNode;
  if (!task.notes) {
    taskTrigger = (
      <button
        type='button'
        className='group flex cursor-pointer items-center gap-1 text-left text-muted-foreground hover:text-foreground'
        aria-label={`Add task notes for ${task.patient_name}`}
        onClick={event => onOpen(task, event)}
      >
        <span>Add note</span>
        <Plus className='h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-70 group-focus-visible:opacity-70' />
      </button>
    );
  } else {
    const trigger = (
      <button
        type='button'
        className='group inline-flex min-w-0 max-w-[9rem] shrink cursor-pointer items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-left text-xs font-medium text-amber-800 hover:bg-amber-100'
        aria-label={`Edit task notes for ${task.patient_name}`}
        data-testid='task-notes-preview'
        onClick={event => onOpen(task, event)}
        onFocus={updateTruncation}
        onMouseEnter={updateTruncation}
      >
        <StickyNote className='h-3 w-3 shrink-0' aria-hidden='true' />
        <span ref={noteRef} className='min-w-0 truncate'>
          {task.notes}
        </span>
        <Pencil className='h-3 w-3 shrink-0 opacity-70' aria-hidden='true' />
      </button>
    );

    taskTrigger = !isTruncated ? (
      trigger
    ) : (
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent
          side='top'
          collisionPadding={12}
          className='max-w-sm whitespace-pre-line'
        >
          {task.notes}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className='flex min-w-0 flex-nowrap items-center gap-1'>
        {taskTrigger}
        {episodeNotes.length > 0 ? (
          <EpisodeNotesPill
            notes={episodeNotes}
            patientName={task.patient_name}
          />
        ) : null}
      </div>
    </TooltipProvider>
  );
};

export default HotboxNotesCell;
